import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerOrdersApi } from '../lib/api';
import { ShoppingCart, Check, AlertCircle, Loader2, HardDrive, Globe, Database, Mail, Shield, Zap } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface Package {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  diskMb: number;
  bandwidthMb: number;
  emailAccounts: number;
  databases: number;
  addonDomains: number;
  hasSsh: boolean;
  hasSsl: boolean;
  hasBackups: boolean;
  hasStaging: boolean;
  priceMonthly: number;
  priceYearly: number | null;
  setupFee: number;
}

type BillingCycle = 'MONTHLY' | 'ANNUALLY';

const cycleLabels: Record<BillingCycle, string> = {
  MONTHLY: 'Monthly',
  ANNUALLY: 'Annually',
};

const cycleMonths: Record<BillingCycle, number> = {
  MONTHLY: 1,
  ANNUALLY: 12,
};

export default function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Order state
  const [step, setStep] = useState<'package' | 'config' | 'checkout'>('package');
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [domain, setDomain] = useState('');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('MONTHLY');
  const [ordering, setOrdering] = useState(false);

  useEffect(() => {
    loadPackages();
  }, []);

  async function loadPackages() {
    try {
      setLoading(true);
      const response = await customerOrdersApi.listPackages();
      setPackages((response.data as any) || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  function calculatePrice(pkg: Package, cycle: BillingCycle): number {
    const months = cycleMonths[cycle];
    if (cycle === 'ANNUALLY' && pkg.priceYearly) {
      return pkg.priceYearly;
    }
    return pkg.priceMonthly * months;
  }

  function calculateSavings(pkg: Package, cycle: BillingCycle): number {
    if (cycle === 'MONTHLY') return 0;
    const regularPrice = pkg.priceMonthly * cycleMonths[cycle];
    const actualPrice = calculatePrice(pkg, cycle);
    return regularPrice - actualPrice;
  }

  function formatCurrency(amount: number) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  }

  function formatStorage(mb: number) {
    if (mb === -1) return 'Unlimited';
    if (mb >= 1024) return `${Math.round(mb / 1024)} GB`;
    return `${mb} MB`;
  }

  function formatCount(count: number) {
    return count === -1 ? 'Unlimited' : count.toString();
  }

  async function handleOrder() {
    if (!selectedPackage || !domain.trim()) return;

    setOrdering(true);
    setError(null);

    try {
      const response = await customerOrdersApi.create({
        packageId: selectedPackage.id,
        domain: domain.trim().toLowerCase(),
        billingCycle,
      });
      
      const invoiceId = (response.data as any)?.invoice?.id;
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}?pay=true`);
      } else {
        navigate('/invoices');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setOrdering(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('orders.orderNew')}</h1>
        <p className="text-gray-600">{t('orders.choosePackage')}</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        {[
          { id: 'package', label: 'Package' },
          { id: 'config', label: 'Configure' },
          { id: 'checkout', label: 'Checkout' },
        ].map((s, i) => (
          <div key={s.id} className="flex items-center">
            {i > 0 && <div className="w-16 h-0.5 bg-gray-200 mr-4" />}
            <div
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium',
                step === s.id
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-100 text-gray-600'
              )}
            >
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">
                {i + 1}
              </span>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Step 1: Package Selection */}
      {step === 'package' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => {
                setSelectedPackage(pkg);
                setStep('config');
              }}
              className={clsx(
                'bg-white rounded-lg shadow p-6 cursor-pointer transition-all hover:shadow-lg border-2',
                selectedPackage?.id === pkg.id
                  ? 'border-brand-600'
                  : 'border-transparent'
              )}
            >
              <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
              {pkg.description && (
                <p className="mt-2 text-gray-600 text-sm">{pkg.description}</p>
              )}

              <div className="mt-4">
                <span className="text-3xl font-bold text-brand-600">
                  {formatCurrency(pkg.priceMonthly)}
                </span>
                <span className="text-gray-500">/month</span>
              </div>

              {pkg.setupFee > 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  + {formatCurrency(pkg.setupFee)} setup fee
                </p>
              )}

              <ul className="mt-6 space-y-3">
                <li className="flex items-center gap-2 text-sm">
                  <HardDrive className="h-4 w-4 text-gray-400" />
                  <span>{formatStorage(pkg.diskMb)} Storage</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-gray-400" />
                  <span>{formatStorage(pkg.bandwidthMb)} Bandwidth</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{formatCount(pkg.emailAccounts)} Email Accounts</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Database className="h-4 w-4 text-gray-400" />
                  <span>{formatCount(pkg.databases)} Databases</span>
                </li>
                {pkg.hasSsl && (
                  <li className="flex items-center gap-2 text-sm text-green-600">
                    <Shield className="h-4 w-4" />
                    <span>{t('orders.freeSsl')}</span>
                  </li>
                )}
                {pkg.hasBackups && (
                  <li className="flex items-center gap-2 text-sm text-green-600">
                    <Zap className="h-4 w-4" />
                    <span>{t('orders.dailyBackups')}</span>
                  </li>
                )}
              </ul>

              <button className="mt-6 w-full py-2 bg-brand-600 text-white rounded-lg font-medium hover:bg-brand-700">
                {t('orders.selectPlan')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Step 2: Configure */}
      {step === 'config' && selectedPackage && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold">{t('orders.configure')}</h2>
                <p className="text-gray-600">{selectedPackage.name}</p>
              </div>
              <button
                onClick={() => setStep('package')}
                className="text-brand-600 hover:text-brand-800 text-sm"
              >
                {t('orders.changePlan')}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('orders.domainName')}
              </label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                {t('orders.domainHelp')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('orders.billingCycle')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                {(['MONTHLY', 'ANNUALLY'] as BillingCycle[]).map((cycle) => {
                  const price = calculatePrice(selectedPackage, cycle);
                  const savings = calculateSavings(selectedPackage, cycle);
                  const monthlyEquivalent = price / cycleMonths[cycle];

                  return (
                    <button
                      key={cycle}
                      onClick={() => setBillingCycle(cycle)}
                      className={clsx(
                        'p-4 rounded-lg border-2 text-left transition-colors',
                        billingCycle === cycle
                          ? 'border-brand-600 bg-brand-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{cycleLabels[cycle]}</span>
                        {billingCycle === cycle && (
                          <Check className="h-5 w-5 text-brand-600" />
                        )}
                      </div>
                      <div className="mt-1">
                        <span className="text-lg font-bold">{formatCurrency(price)}</span>
                        {cycle !== 'MONTHLY' && (
                          <span className="text-sm text-gray-500 ml-1">
                            ({formatCurrency(monthlyEquivalent)}/mo)
                          </span>
                        )}
                      </div>
                      {savings > 0 && (
                        <div className="mt-1 text-sm text-green-600 font-medium">
                          Save {formatCurrency(savings)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep('package')}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('orders.back')}
              </button>
              <button
                onClick={() => setStep('checkout')}
                disabled={!domain.trim()}
                className={clsx(
                  'px-6 py-2 rounded-lg font-medium text-white transition-colors',
                  domain.trim()
                    ? 'bg-brand-600 hover:bg-brand-700'
                    : 'bg-gray-400 cursor-not-allowed'
                )}
              >
                {t('orders.continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Checkout */}
      {step === 'checkout' && selectedPackage && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6 space-y-6">
            <h2 className="text-lg font-semibold pb-4 border-b border-gray-200">
              {t('orders.summary')}
            </h2>

            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{selectedPackage.name}</p>
                  <p className="text-sm text-gray-500">{domain}</p>
                  <p className="text-sm text-gray-500">{cycleLabels[billingCycle]}</p>
                </div>
                <span className="font-medium">
                  {formatCurrency(calculatePrice(selectedPackage, billingCycle))}
                </span>
              </div>

              {selectedPackage.setupFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('orders.setupFee')}</span>
                  <span>{formatCurrency(selectedPackage.setupFee)}</span>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                <span className="font-semibold text-lg">{t('orders.totalToday')}</span>
                <span className="font-bold text-xl text-brand-600">
                  {formatCurrency(
                    calculatePrice(selectedPackage, billingCycle) + selectedPackage.setupFee
                  )}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium mb-2">{t('orders.whatNext')}</h3>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>{t('orders.nextPayment')}</li>
                <li>{t('orders.nextSetup')}</li>
                <li>{t('orders.nextEmail')}</li>
              </ol>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep('config')}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('orders.back')}
              </button>
              <button
                onClick={handleOrder}
                disabled={ordering}
                className={clsx(
                  'inline-flex items-center gap-2 px-6 py-2 rounded-lg font-medium text-white transition-colors',
                  ordering ? 'bg-gray-400' : 'bg-brand-600 hover:bg-brand-700'
                )}
              >
                {ordering ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ShoppingCart className="h-5 w-5" />
                )}
                Place Order & Pay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
